import { useState, useEffect } from "react";
import { BookOpen } from "lucide-react";
import { useClasseContext } from "../context/ClasseContext";
import { getReussiteSavoir, getReussiteDescripteur, getReussiteTheme } from "../utils/ClasseEvaluations";
import AcquisitionSavoirs from "../components/VueSavoirs/AcquisitionSavoirs";
import AcquisitionSavoirsCards from "../components/VueSavoirs/AcquisitionSavoirsCards";
import PilotageHeader from "../components/general/PilotageHeader";
import { niveauDeLaClasse } from "../components/CollerNotes/PastilleNiveau";

function Savoirs({ eleves, savoirfromDashboard }) {
  const { classeNom, groupNom, ActivePeriode } = useClasseContext();
  const [savoirs, setSavoirs] = useState();
  const [descripteurs, setDescripteurs] = useState();
  const [themes, setThemes] = useState();
  const [viewLevel, setViewLevel] = useState('savoir');
  const [selectedSavoir, setSelectedSavoir] = useState(savoirfromDashboard);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (eleves.eleves) {
      setSavoirs(getReussiteSavoir(eleves, ActivePeriode));
      setDescripteurs(getReussiteDescripteur(eleves, ActivePeriode));
      setThemes(getReussiteTheme(eleves, ActivePeriode));
    }
  }, [eleves, ActivePeriode]);

  if (eleves && eleves.eleves && eleves.eleves.length === 0) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-md text-left mb-5 h-fit">
        <p className="xl:text-base lg:text-sm text-xs p-2">
          Aucun élève n'a été détecté pour cette classe dans TanoEd.
          <br /> Veuillez vous rendre dans le menu <span className="font-bold">Dashboard </span> pour importer un fichier Excel contenant les noms et prénoms des élèves de votre classe.
        </p>
      </div>
    );
  }

  // Stats
  const totalEvaluations = savoirs && savoirs.reduce((sum, s) => sum + s.evalDates.size, 0);
  const themesUniques = savoirs && [...new Set(savoirs.map(s => s.themeNom))].length;

  // Items à afficher selon l'onglet actif
  const currentItems = viewLevel === 'theme' ? themes
                     : viewLevel === 'descripteur' ? descripteurs
                     : savoirs;

  return (
    <div className="px-4 flex flex-col xl:text-xs 2xl:text-sm 3xl:text-base lg:text-sm text-xs text-black p-2">
      <div className="rounded-2xl">
        {/* En-tête standard Pilotage */}
        <PilotageHeader
          icone={BookOpen}
          titre="Savoirs évalués"
          sousTitre={classeNom ? "Classe de " + classeNom : "Groupe " + groupNom}
          badgeNiveau={classeNom?.charAt(0)}
          stats={[
            { label: "Savoirs", valeur: savoirs ? savoirs.length : '-' },
            { label: "Évaluations", valeur: totalEvaluations || '-' },
            { label: "Thèmes", valeur: themesUniques || '-' },
          ]}
        />

        {/* Onglets Thèmes / Notions / Savoirs */}
        {!selectedSavoir && (
          <div className="inline-flex gap-1 bg-white rounded-xl border border-slate-200 shadow-sm p-1 mb-4 w-fit">
            {[
              { key: 'theme', label: 'Thèmes' },
              { key: 'descripteur', label: 'Notions' },
              { key: 'savoir', label: 'Savoirs' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => { setViewLevel(tab.key); setSelectedSavoir(undefined); setSearchTerm(''); }}
                className={`px-4 py-1.5 rounded-lg text-[13px] font-medium transition-colors duration-200 whitespace-nowrap ${
                  viewLevel === tab.key
                    ? 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white shadow-md shadow-blue-500/30'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Vue Cards (tous les niveaux) */}
        {!selectedSavoir && currentItems && (
          <AcquisitionSavoirsCards
            items={currentItems}
            viewLevel={viewLevel}
            niveauClasse={niveauDeLaClasse(classeNom || groupNom, eleves?.eleves)}
            onCardClick={(item) => setSelectedSavoir(item)}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        )}

        {/* Détail par élève */}
        {selectedSavoir && (
          <AcquisitionSavoirs
            selectedSavoir={selectedSavoir}
            setSelectedSavoir={setSelectedSavoir}
            eleves={eleves}
            viewLevel={viewLevel}
          />
        )}
      </div>
    </div>
  );
}

export default Savoirs;
